import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { ChevronRight, FileText, Search } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface DocEntry {
  id: string;
  title: string;
  file: string;
  category: string;
}

const DOCS_LIST: DocEntry[] = [
  { id: "qa-paywall", title: "QA — Paywall y lógica de acceso", file: "qa-paywall.md", category: "QA / Testing" },
  { id: "guide-auth", title: "Registro e Inicio de Sesión", file: "guide-auth.md", category: "Guías de Usuario" },
  { id: "guide-catalog", title: "Explorar el Catálogo", file: "guide-catalog.md", category: "Guías de Usuario" },
  { id: "guide-player", title: "Reproductor de Audio", file: "guide-player.md", category: "Guías de Usuario" },
  { id: "guide-library", title: "Biblioteca y Progreso", file: "guide-library.md", category: "Guías de Usuario" },
  { id: "guide-chat", title: "Chat con el Asistente IA", file: "guide-chat.md", category: "Guías de Usuario" },
  { id: "overview", title: "Visión General", file: "overview.md", category: "General" },
  { id: "architecture", title: "Arquitectura del Sistema", file: "architecture.md", category: "General" },
  { id: "database", title: "Base de Datos", file: "database.md", category: "Backend" },
  { id: "edge-functions", title: "Edge Functions", file: "edge-functions.md", category: "Backend" },
  { id: "supabase-config", title: "Configuración Supabase", file: "supabase-config.md", category: "Backend" },
  { id: "guide-admin-rag", title: "Procesar contenido para IA (Admin)", file: "guide-admin-rag.md", category: "IA / RAG" },
  { id: "rag", title: "Cómo funciona el RAG", file: "rag.md", category: "IA / RAG" },
  { id: "content-processing", title: "Procesamiento de contenido", file: "content-processing.md", category: "IA / RAG" },
  { id: "chat-ia", title: "Chat IA", file: "chat-ia.md", category: "IA / RAG" },
  { id: "mobile-guide", title: "Guía de la App", file: "mobile-guide.md", category: "App Móvil" },
  { id: "mobile-navigation", title: "Navegación", file: "mobile-navigation.md", category: "App Móvil" },
  { id: "api-endpoints", title: "Endpoints disponibles", file: "api-endpoints.md", category: "API" },
  { id: "analytics", title: "Analytics y Funnels", file: "analytics.md", category: "Analytics" },
];

const CATEGORIES = ["QA / Testing", "Guías de Usuario", "General", "Backend", "IA / RAG", "App Móvil", "API", "Analytics"];

const markdownComponents = {
  h1: ({ ...props }) => <h1 className="text-3xl font-heading font-bold mt-8 mb-4 border-b pb-2" {...props} />,
  h2: ({ ...props }) => <h2 className="text-2xl font-heading font-semibold mt-6 mb-3" {...props} />,
  h3: ({ ...props }) => <h3 className="text-xl font-heading font-medium mt-4 mb-2" {...props} />,
  h4: ({ ...props }) => <h4 className="text-lg font-heading font-medium mt-3 mb-1" {...props} />,
  p: ({ ...props }) => <p className="leading-7 mb-4 text-muted-foreground" {...props} />,
  ul: ({ ...props }) => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />,
  ol: ({ ...props }) => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />,
  li: ({ ...props }) => <li className="text-muted-foreground" {...props} />,
  a: ({ ...props }) => <a className="text-primary hover:underline" {...props} />,
  code: ({ className, children, ...props }: any) => {
    const isBlock = className?.includes("language-");
    if (isBlock) {
      return <code className={cn("text-sm font-mono", className)} {...props}>{children}</code>;
    }
    return <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono text-primary" {...props}>{children}</code>;
  },
  pre: ({ ...props }) => (
    <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-6 border border-slate-800 text-sm" {...props} />
  ),
  blockquote: ({ ...props }) => (
    <blockquote className="border-l-4 border-primary pl-4 italic my-4 text-muted-foreground" {...props} />
  ),
  table: ({ ...props }) => (
    <div className="my-6 w-full overflow-x-auto">
      <table className="w-full border-collapse border border-border text-sm" {...props} />
    </div>
  ),
  th: ({ ...props }) => <th className="border border-border bg-muted px-4 py-2 font-bold text-left" {...props} />,
  td: ({ ...props }) => <td className="border border-border px-4 py-2 text-muted-foreground" {...props} />,
  hr: ({ ...props }) => <hr className="my-6 border-border" {...props} />,
};

export default function DocsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const docId = searchParams.get("doc") || "guide-auth";
  const selectedDoc = DOCS_LIST.find((d) => d.id === docId) || DOCS_LIST[0];

  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    fetch(`/docs/${selectedDoc.file}`)
      .then((r) => (r.ok ? r.text() : "# Error\nNo se pudo cargar el documento."))
      .then((text) => setContent(text))
      .catch(() => setContent("# Error\nOcurrió un error al cargar el documento."))
      .finally(() => setLoading(false));
  }, [selectedDoc.file]);

  const filtered = DOCS_LIST.filter(
    (d) =>
      d.title.toLowerCase().includes(search.toLowerCase()) ||
      d.category.toLowerCase().includes(search.toLowerCase())
  );

  const selectDoc = (doc: DocEntry) => {
    setSearchParams({ doc: doc.id });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="border-b bg-card px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-sm text-primary hover:underline">
            &larr; Senda de Vida
          </Link>
          <nav className="flex gap-4 text-sm">
            <Link to="/docs" className="text-foreground font-medium">
              Documentación
            </Link>
            <Link to="/legal/terms" className="text-muted-foreground hover:text-foreground">
              Términos
            </Link>
            <Link to="/legal/privacy" className="text-muted-foreground hover:text-foreground">
              Privacidad
            </Link>
          </nav>
        </div>
      </header>

      {/* Main */}
      <div className="flex flex-col lg:flex-row flex-1 max-w-7xl mx-auto w-full">
        {/* Sidebar */}
        <aside className="w-full lg:w-72 border-b lg:border-b-0 lg:border-r bg-card flex flex-col">
          <div className="p-4 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar documento..."
                className="pl-8"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <ScrollArea className="flex-1 max-h-[30vh] lg:max-h-[calc(100vh-10rem)]">
            <div className="p-4 space-y-6">
              {CATEGORIES.map((category) => {
                const categoryDocs = filtered.filter((d) => d.category === category);
                if (categoryDocs.length === 0) return null;

                return (
                  <div key={category} className="space-y-1">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2 mb-2">
                      {category}
                    </h3>
                    {categoryDocs.map((doc) => (
                      <button
                        key={doc.id}
                        onClick={() => selectDoc(doc)}
                        className={cn(
                          "w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md transition-colors",
                          selectedDoc.id === doc.id
                            ? "bg-primary text-primary-foreground"
                            : "hover:bg-accent text-muted-foreground hover:text-accent-foreground"
                        )}
                      >
                        <FileText className="h-4 w-4 shrink-0" />
                        <span className="truncate text-left">{doc.title}</span>
                        {selectedDoc.id === doc.id && <ChevronRight className="h-4 w-4 ml-auto shrink-0" />}
                      </button>
                    ))}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </aside>

        {/* Content */}
        <main className="flex-1 overflow-hidden">
          <ScrollArea className="h-[calc(100vh-4rem)]">
            <div className="max-w-4xl mx-auto p-8 lg:p-12">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <span>Documentación</span>
                <ChevronRight className="h-3 w-3" />
                <span>{selectedDoc.category}</span>
              </div>
              <h1 className="text-4xl font-heading font-extrabold tracking-tight mb-8">
                {selectedDoc.title}
              </h1>
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20 space-y-4">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  <p className="text-muted-foreground animate-pulse">Cargando contenido...</p>
                </div>
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents as any}>
                  {content}
                </ReactMarkdown>
              )}
            </div>
          </ScrollArea>
        </main>
      </div>

      {/* Footer */}
      <footer className="border-t px-6 py-4 text-center text-xs text-muted-foreground">
        &copy; {new Date().getFullYear()} Senda de Vida. Todos los derechos reservados.
      </footer>
    </div>
  );
}
